/**
 * Custom hook for determining drag-drop scenario and its metadata
 * Makes scenario logic reusable across different components
 */

import { useMemo } from 'react';
import type { DragDropContext } from '@consolidocs-ui/utils';
import { determineScenario, getScenarioConfig, ScenarioType, ScenarioConfig } from '../types/dragDropScenarios';

export interface UseDragDropScenarioResult {
  scenario: ScenarioType;
  config: ScenarioConfig;
}

/**
 * Hook to determine the current drag-drop scenario and retrieve its configuration
 * @param context - The drag-drop context with source/destination information
 * @returns Scenario type and its configuration
 */
export const useDragDropScenario = (
  context?: DragDropContext
): UseDragDropScenarioResult => {
  return useMemo(() => {
    if (!context) {
      const scenario: ScenarioType = 'unknown';
      return {
        scenario,
        config: getScenarioConfig(scenario),
      };
    }

    const scenario = determineScenario(
      context.sourceBucket,
      context.targetBucket,
      context.sourceContractTitle,
      context.destContractTitle,
      context.itemType
    );

    return {
      scenario,
      config: getScenarioConfig(scenario),
    };
  }, [context]);
};
